import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { Goal } from '@bingads-webui/campaign-odata-schemas';
import { CampaignGoalLevelEditor } from './goal-scope-selector';

const whitelistedGoalProperties = _.keys(Goal.properties);
const cleanGoal = goal => _.pick(goal, '@odata.type', ...whitelistedGoalProperties);

export const ConversionGoalSelector = (props) => {
  const {
    isSelectorEnabled,
    checkSelected,
    pageSize,
    ariaLabel,
    searchBoxAriaLabel,
    isMultiMode,
    showTrackingStatusIcon,
    isHideSaveBar,
    i18n,
    // TODO need to pass this function in if it's in setting mode
    turnOnRemoveAlert,
    $dataRange,
    cid,
    aid,
    goalSelection,
    campaignConversionGoal,
    onChange,
    activityLogger,
    subTextOptions,
  } = props;

  const onSelectDone = useCallback((selection) => {
    const cleanedUpSelection = selection.map(cleanGoal);
    onChange({
      goalSelection: cleanedUpSelection,
    });
  }, [onChange]);

  const onScopeChange = useCallback((scope) => {
    onChange({
      goalScopeSelection: scope,
    });
  }, [onChange]);

  const fetch = useCallback(({ query, pageNumber }) => {
    const formatDate = date => i18n.formatDate(date, { raw: 'M/d/yyyy' });

    return $dataRange.then((dateRange) => {
      const select =['Id', 'Name', 'GoalCategory', 'Status'];
      if (showTrackingStatusIcon) {
        select.push('TrackingStatus', 'Type');
      }
      const dataQuery = {
        top: pageSize,
        skip: pageNumber * pageSize,
        startdate: formatDate(dateRange.start),
        enddate: formatDate(dateRange.end),
        filter: '(Status eq Enum.GoalStatus\'Active\') and ((GoalCategory eq Enum.GoalCategory\'InStoreVisit\') eq false)',
        count: true,
        select,
      };

      if (query || query === '') {
        dataQuery.filter = {
          where: `${dataQuery.filter} and contains(tolower(Name), tolower('${query}'))`,
        };
      }

      return edmCampaign
        .Customers.$withKey(cid)
        .Accounts.$withKey(aid)
        .Goals
        .get({
          ...dataQuery,
        }).toPromise().then(({
          value: cmps,
          '@odata.count': totalCount,
        }) => {
          const items = _.compact(_.map(cmps, (cmp) => {
            const baseItem = {
              Id: cmp.Id,
              Name: cmp.Name,
              GoalCategory: cmp.GoalCategory,
              Status: cmp.Status,
            };

            // Add TrackingStatus if the condition is met
            if (showTrackingStatusIcon) {
              baseItem.TrackingStatus = cmp.TrackingStatus;
              baseItem.Type = cmp.Type;
            }
            return baseItem;
          }));

          return {
            items,
            totalCount,
          };
        })
        .catch(() =>
          ({
            items: [],
            totalCount: 0,
          }));
    });
  }, [$dataRange, aid, cid, i18n, pageSize]);
  return isSelectorEnabled ?
    (
      <CampaignGoalLevelEditor
        i18n={i18n}
        fetch={fetch}
        onSelectDone={onSelectDone}
        selectedItem={null}
        selectedItemsForMultiMode={goalSelection}
        checkSelected={checkSelected}
        pageSize={pageSize}
        ariaLabel={ariaLabel}
        searchBoxAriaLabel={searchBoxAriaLabel}
        isMultiMode={isMultiMode}
        showTrackingStatusIcon={showTrackingStatusIcon}
        isHideSaveBar={isHideSaveBar}
        campaignConversionGoal={campaignConversionGoal}
        onScopeChange={onScopeChange}
        turnOnRemoveAlert={turnOnRemoveAlert}
        activityLogger={activityLogger}
        subTextOptions={subTextOptions}
      />
    ) : (<div className="disable-goal-selector" />);
};

ConversionGoalSelector.propTypes = {
  /**
   * item count per page
   */
  pageSize: PropTypes.number,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * Used to check whether an item is selected.
   *
   * @param {Object} selectedItem - selected item
   * @param {Object} item - single data in the list
   * @return {boolean} - whether current item is selected
   */
  checkSelected: PropTypes.func,
  /**
   * judge whhether campaign selector is used as adgroup selector
   */
  campaign: PropTypes.shape({
    CampaignConversionGoal: PropTypes.shape({}),
    Id: PropTypes.number,
  }),
  /**
    * the campaign adgroup selector mode: multi or single
    */
  isMultiMode: PropTypes.bool,
  /**
   * should show tracking status icon
   */
  showTrackingStatusIcon: PropTypes.bool,
  /**
    * aria-label for campaign list selector
    */
  ariaLabel: PropTypes.string,
  /**
   * aria label of search box input
   */
  searchBoxAriaLabel: PropTypes.string,
  /**
   * customize OriginCampaignSelector
   */
  sourceGoalSelectorOptions: PropTypes.shape({}),
  /**
   * message bar configuration
   */
  messageConfig: PropTypes.shape({}),
  /**
   * if action bar (done & cancel) should render
   */
  isHideSaveBar: PropTypes.bool,
  /**
   * Promise to get date range
   */
  $dataRange: PropTypes.shape({
    then: PropTypes.func,
  }).isRequired,
  /**
   * View model data
   */
  goalSelection: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Original set of goals
   */
  campaignConversionGoal: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Method to update view model data
   */
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.shape({}),
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  isSelectorEnabled: PropTypes.bool.isRequired,
  turnOnRemoveAlert: PropTypes.func,
  activityLogger: PropTypes.shape({}),
  subTextOptions: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
  }),
};

ConversionGoalSelector.defaultProps = {
  pageSize: 10,
  checkSelected: (selectedItem, item) => _.isEqual(selectedItem, item),
  campaign: null,
  isMultiMode: false,
  showTrackingStatusIcon: false,
  ariaLabel: '',
  searchBoxAriaLabel: '',
  sourceGoalSelectorOptions: {},
  messageConfig: {},
  isHideSaveBar: false,
  classes: {},
  turnOnRemoveAlert: _.noop,
  goalSelection: [],
  campaignConversionGoal: [],
  activityLogger: null,
  subTextOptions: null,
};

